import { measureTextHeight, opacityToColor } from "./Data_Processing";
import {
  createSimulation,
  dragstarted,
  dragended,
  dragged,
} from "./Animations";
import * as d3 from "d3";

export const getImageDimensions = (imgSrc) => {
  return new Promise((resolve) => {
    if (!imgSrc) {
      resolve({ width: 0, height: 0 });
    }
    const imgAspect = new Image();
    imgAspect.src = imgSrc;
    imgAspect.onload = () => {
      resolve({ width: imgAspect.width, height: imgAspect.height });
    };
  });
};

export const createNodes = async (thoughts) => {
  const data = { nodes: [], links: [] };
  const labelDict = {};
  const nodeDict = {};

  // shared labels
  const thoughtDict = {};

  for (let i = 0; i < thoughts.length; i++) {
    let labels = thoughts[i].label ? thoughts[i].label.split(", ") : [];
    let type = thoughts[i].type;
    let recent = thoughts[i].recent;
    let img = null;
    let content = null;
    if (recent === 0 && thoughts[i].label) {
      labels = [];
    }
    if (type === "image") {
      img = thoughts[i].thumbnailUrl
        ? thoughts[i].thumbnailUrl
        : thoughts[i].url;
    } else if (type === "link") {
      img = thoughts[i].thumbnailUrl
        ? thoughts[i].thumbnailUrl
        : thoughts[i].imgUrl;
    } else if (type === "rich_text" || type === "text") {
      let parsed = thoughts[i].preview;
      if (
        parsed &&
        parsed.blocks &&
        parsed.blocks[0] &&
        parsed.blocks[0].data
      ) {
        content = parsed.blocks[0].data.text;
      } else if (thoughts[i].content) {
        content = thoughts[i].content;
      }
    } else {
      content = thoughts[i].content;
    }
    thoughtDict[thoughts[i].id] = thoughts[i];

    const { width, height } = await getImageDimensions(img);

    data.nodes.push({
      id: thoughts[i].id,
      img: img,
      imgH: height,
      imgW: width,
      recent: recent,
      labels: labels ? labels : [],
      content: content,
    });
    nodeDict[thoughts[i].id] = data.nodes[data.nodes.length - 1];

    // sort nodes by recent
    data.nodes.sort((a, b) => {
      return a.recent - b.recent;
    });

    if (labels && labels.length > 0 && recent > 0) {
      for (let j = 0; j < labels.length; j++) {
        if (labelDict[labels[j]]) {
          labelDict[labels[j]].push(thoughts[i].id);
        } else {
          labelDict[labels[j]] = [thoughts[i].id];
        }
      }
    }
  }

  // create links
  for (let label in labelDict) {
    let ids = labelDict[label];
    if (ids.length === 1) continue;
    for (let i = 0; i < ids.length; i++) {
      for (let j = i + 1; j < ids.length; j++) {
        // check opacity of linkthoughts
        let opacitySource, opacityTarget;
        opacitySource = nodeDict[ids[i]].recent;
        opacityTarget = nodeDict[ids[j]].recent;
        data.links.push({
          source: nodeDict[ids[i]],
          target: nodeDict[ids[j]],
          recent: (opacitySource + opacityTarget) / 2,
        });
      }
    }
  }

  return { data: data, labelDict: labelDict };
};

export function processNodes(data, constants) {
  const { centerX, centerY, textWidth, fontSize } = constants;

  // Distribute nodes in a more spread out initial position
  const radius = Math.min(constants.width, constants.height) / 2.5;
  data.nodes.forEach((node, i) => {
    const angle = (i / data.nodes.length) * 2 * Math.PI;
    node.x = centerX + radius * Math.cos(angle);
    node.y = centerY + radius * Math.sin(angle);
    if (!node.img && node.content) {
      node.textHeight =
        measureTextHeight(node.content, textWidth, fontSize) + 25;
    }
  });

  return data;
}

export function drawNodes(svg, constants, sharedFeatures) {
  let wasMoved = false;
  let simulation;
  let drag = 0;
  let { textWidth, imgWidth, imgHeight, fontSize } = constants;
  let { selection, data, setSidebar, setImgRatio, clusters, clusterCenters } =
    sharedFeatures;

  // Create nodes (images)
  const nodes = svg
    .selectAll(".node")
    .data(data.nodes)
    .enter()
    .filter(function (d) {
      if (selection === "all") return true;
      else {
        for (let i = 0; i < d.labels.length; i++) {
          if (d.labels[i] === selection) return true;
        }
      }
      return false;
    })
    .append("g")
    .attr("class", "node")
    .attr("id", (d) => "node_" + d.id)
    .call(
      d3
        .drag()
        .on("start", (e, d) => {
          drag = 0;
          dragstarted(e, d, data, simulation);
        })
        .on("drag", (e, d) => {
          drag = dragged(e, d, drag, wasMoved);
          if (drag > 2) wasMoved = true;
        })
        .on("end", (e, d) => {
          dragended(
            e,
            d,
            setSidebar,
            setImgRatio,
            opacityToColor,
            wasMoved,
            simulation,
            data
          );
          wasMoved = false;
        })
    );

  nodes
    .filter((d) => d.img)
    .append("image")
    .attr("class", "node-image")
    .attr("xlink:href", (d) => d.img)
    .attr("width", (d) => imgWidth)
    .attr("height", (d) => imgHeight)
    .style("opacity", (d) => d.recent)
    .attr("transform", (d) => `translate(${-imgWidth / 2}, ${-imgHeight / 2})`);

  simulation = createSimulation(data, constants, svg, clusterCenters);

  nodes
    .filter((d) => !d.img && d.content && !d.isLabel)
    .append("rect")
    .attr("class", "node-text")
    .attr("width", textWidth + 50)
    .attr("height", (d) => d.textHeight)
    .attr("x", (-50 - textWidth) / 2)
    .attr("y", (d) => -d.textHeight / 2)
    .attr("rx", 5)
    .attr("ry", 5)
    .style("fill", (d) => `rgb(255, 255, 255, ${d.recent})`)
    .style("stroke", (d) => opacityToColor(d.recent));

  nodes
    .filter((d) => !d.img && d.content)
    .append("foreignObject")
    .attr("id", (d) => "foreign_" + d.id)
    .attr("font-size", fontSize)
    .attr("width", textWidth)
    .attr("height", (d) => d.textHeight)
    .attr("x", (d) => -textWidth / 2)
    .attr("y", (d) => -d.textHeight / 2)
    .append("xhtml:div")
    .attr("class", "text-container node-text")
    .attr("id", (d) => "text_" + d.id)
    .attr("key", (d) => "text_" + d.id)
    .style("width", `${textWidth}px`)
    .style("margin", "auto")
    .style("stroke", (d) => opacityToColor(d.recent))
    .style("color", (d) => opacityToColor(d.recent))
    .html((d) => {
      return d.content;
    });

  // iterate over all xhml divs and calculate padding, workaround for safari
  d3.selectAll(".text-container").each(function (d) {
    let div = d3.select(this);
    let id = div.attr("id").split("_")[1];
    let height = div.node().offsetHeight;
    let parentDiv = d3.select(`#foreign_${id}`);
    let parentHeight = parentDiv.node().clientHeight;
    let padding = (parentHeight - height) / 2;
    div.style("padding-top", `${padding}px`);
  });

  // filter clusterCenters only to clusters with mroe than length 1
  let clusterCentersFiltered = {};
  Object.entries(clusterCenters).forEach(([key, value]) => {
    if (clusters[key].length > 1) {
      clusterCentersFiltered[key] = value;
    }
  });
}
